// components/TokenDashboard.jsx
import { useState, useEffect, useRef } from "react";
import JourneyPaths from "./JourneyPaths";
import './reid.css';

const Reid = ({
  selectedSite: propSelectedSite,
  selectedBusiness: propSelectedBusiness,
  selectedYear: propSelectedYear,
}) => {
  const [journeyData, setJourneyData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [defaultRange, setDefaultRange] = useState(null);
  const abortControllerRef = useRef(null);
  const cacheRef = useRef({});

  // Use props from parent Analytics component if provided, otherwise use localStorage
  const selectedSite = propSelectedSite || JSON.parse(localStorage.getItem("analytics_selected_site") || "null");
  const selectedBusiness = propSelectedBusiness || localStorage.getItem("analytics_selected_business") || "";
  const selectedYear = propSelectedYear || parseInt(localStorage.getItem("analytics_selected_year")) || new Date().getFullYear();

  // console.log("Reid component - using selections:", {
  //   selectedSite,
  //   selectedBusiness,
  //   selectedYear,
  //   fromProps: !!propSelectedSite
  // });

  // Compute the full-month range for a given YYYY-MM-DD date string
  const getMonthRange = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  const fetchJourneyData = async (startDate, endDate) => {
    const cacheKey = `${selectedSite?.hwid}-${startDate}-${endDate}`;

    // Return cached result if available
    if (cacheRef.current[cacheKey]) {
      setJourneyData(cacheRef.current[cacheKey]);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const requestBody = {
        hw_ids: selectedSite?.hwid ? [selectedSite.hwid] : null
      };

      const url = `${process.env.REACT_APP_API_BASE_URL}/journey/summary/${startDate}/${endDate}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract the data from the API response (server already aggregated it)
      const extractedData = extractJourneyData(data);
      // console.log("Extracted data", extractedData);
      cacheRef.current[cacheKey] = extractedData;
      setJourneyData(extractedData);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching journey data:", error);
      setError(error.message || "Failed to fetch journey data");
      setJourneyData({});
    } finally {
      setIsLoading(false);
    }
  };

  // ... keep all existing helper functions unchanged ...
  const extractJourneyData = (rawData) => {
    // console.log("extractJourneyData input:", rawData);
    
    if (!rawData || Object.keys(rawData).length === 0) {
      // console.log("No raw data found");
      return {};
    }

    // The API returns data keyed by date, but we just need the first (and likely only) date's data
    const dateKeys = Object.keys(rawData);
    // console.log("Available date keys:", dateKeys);
    
    if (dateKeys.length === 0) {
      // console.log("No date keys found in response");
      return {};
    }

    // Take the first date's data (for date ranges, server aggregates it under one date)
    const firstDateKey = dateKeys[0];
    const dayData = rawData[firstDateKey];

    if (!dayData) {
      // console.log("No data found for date:", firstDateKey);
      return {};
    }

    // Transform the new array-based data structure to include zone IDs
    const transformedData = {
      dwellPathsByReid: dayData.dwellPathsByReid || {},
      averageDwellTimePerZone: transformAverageDwellTimes(dayData.averageDwellTimePerZone || []),
      zoneTransitionProbabilities: transformZoneTransitions(dayData.zoneTransitionProbabilities || []),
      // Add zone metadata for mapping IDs to names and vice versa
      zoneMetadata: extractZoneMetadata(dayData)
    };

    return transformedData;
  };

  // Extract zone metadata (ID to name mapping) from API response
  const extractZoneMetadata = (dayData) => {
    const zoneMetadata = {
      zoneIdToName: {},  // zone_id -> zone_name
      zoneNameToId: {},  // zone_name -> zone_id
      zones: []          // Array of zone objects with id and name
    };

    // Extract from averageDwellTimePerZone
    if (dayData.averageDwellTimePerZone) {
      dayData.averageDwellTimePerZone.forEach(item => {
        if (item.zone_id && item.zone_name) {
          zoneMetadata.zoneIdToName[item.zone_id] = item.zone_name;
          zoneMetadata.zoneNameToId[item.zone_name] = item.zone_id;
          
          // Add to zones array if not already present
          if (!zoneMetadata.zones.find(z => z.id === item.zone_id)) {
            zoneMetadata.zones.push({
              id: item.zone_id,
              name: item.zone_name
            });
          }
        }
      });
    }

    // Extract from zoneTransitionProbabilities
    if (dayData.zoneTransitionProbabilities) {
      dayData.zoneTransitionProbabilities.forEach(transition => {
        // From zone
        if (transition.from_zone_id && transition.from_zone_name) {
          zoneMetadata.zoneIdToName[transition.from_zone_id] = transition.from_zone_name;
          zoneMetadata.zoneNameToId[transition.from_zone_name] = transition.from_zone_id;
          
          if (!zoneMetadata.zones.find(z => z.id === transition.from_zone_id)) {
            zoneMetadata.zones.push({
              id: transition.from_zone_id,
              name: transition.from_zone_name
            });
          }
        }

        // To zone
        if (transition.to_zone_id && transition.to_zone_name) {
          zoneMetadata.zoneIdToName[transition.to_zone_id] = transition.to_zone_name;
          zoneMetadata.zoneNameToId[transition.to_zone_name] = transition.to_zone_id;
          
          if (!zoneMetadata.zones.find(z => z.id === transition.to_zone_id)) {
            zoneMetadata.zones.push({
              id: transition.to_zone_id,
              name: transition.to_zone_name
            });
          }
        }
      });
    }

    return zoneMetadata;
  };

  // Transform averageDwellTimePerZone - keep using zone_name for backwards compatibility
  // but also provide zone_id mapping
  const transformAverageDwellTimes = (dwellTimesArray) => {
    const dwellTimesObject = {};
    
    dwellTimesArray.forEach(item => {
      if (item.zone_name && typeof item.average_dwell_time === 'number') {
        dwellTimesObject[item.zone_name] = item.average_dwell_time;
      }
    });
    
    // console.log("Transformed dwell times:", dwellTimesObject);
    return dwellTimesObject;
  };

  // Transform zoneTransitionProbabilities - keep using zone_name for backwards compatibility
  // but also provide zone_id mapping
  const transformZoneTransitions = (transitionsArray) => {
    const transitionsObject = {};
    
    transitionsArray.forEach(transition => {
      const fromZone = transition.from_zone_name;
      const toZone = transition.to_zone_name;
      const probability = transition.probability;
      
      if (fromZone && toZone && typeof probability === 'number') {
        // Initialize the from zone if it doesn't exist
        if (!transitionsObject[fromZone]) {
          transitionsObject[fromZone] = {};
        }
        
        // Add the transition probability
        transitionsObject[fromZone][toZone] = probability;
      }
    });
    
    return transitionsObject;
  };

  // When site changes, find the latest available data date and use that month as the default range
  useEffect(() => {
    if (!selectedSite?.hwid) {
      setDefaultRange(null);
      return;
    }

    setDefaultRange(null);

    const fetchLatestDate = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/journey/latest-date`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hw_ids: [selectedSite.hwid] }),
        });
        if (response.ok) {
          const { latest_date } = await response.json();
          if (latest_date) {
            setDefaultRange(getMonthRange(latest_date));
            return;
          }
        }
      } catch (e) {
        // fall through to current-month fallback
      }
      setDefaultRange(getMonthRange(new Date().toISOString().split('T')[0]));
    };

    fetchLatestDate();
  }, [selectedSite?.hwid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="reid-container">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="reid-loading-overlay">
          <div className="reid-loading-content">
            <div className="reid-loading-spinner"></div>
            <div className="reid-loading-text">
              Fetching journey data...
            </div>
            <div className="reid-loading-subtext">
              Analyzing zone transitions and dwell times
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !isLoading && (
        <div className="reid-error-overlay">
          <div className="reid-error-content">
            <div className="reid-error-icon">⚠️</div>
            <div className="reid-error-text">Failed to load data</div>
            <div className="reid-error-subtext">{error}</div>
            <button 
              className="reid-error-retry"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <JourneyPaths
        journeyData={journeyData}
        selectedYear={selectedYear}
        selectedSite={selectedSite}
        onDateRangeChange={fetchJourneyData}
        isLoading={isLoading}
        initialDateRange={defaultRange}
      />
    </div>
  );
};

export default Reid;